import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
    convertIdsToDisplay as convertIdsToDisplayUtil,
    convertDisplayToIds as convertDisplayToIdsUtil,
    migrateOldVariableFormat,
} from '../../../../utils/automation/variableUtils';
import {
    calculateDropdownPosition,
    findFileRefAtCursor,
    findFileSegmentInDisplay,
    findVariableAtCursor,
    getDropdownState,
} from './smartInputHelpers';
import useSmartInputInsertions from './useSmartInputInsertions';

export default function useSmartInputBox({
    variableTrigger = '${',
    suggestions = [],
    descriptions = {},
    fileTrigger = '',
    fileSuggestions = [],
    formatFileInsertion,
    value = '',
    onChange,
    onBlur,
    onKeyDown: parentOnKeyDown,
    onMultiLinePaste,
    readOnly = false,
    disabled = false,
}) {
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownMode, setDropdownMode] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const inputRef = useRef(null);
    const dropdownListRef = useRef(null);
    const dropdownPortalRef = useRef(null);
    const containerRef = useRef(null);
    const migratedValueRef = useRef(null);

    const activeList = dropdownMode === 'variables' ? suggestions : dropdownMode === 'files' ? fileSuggestions : [];
    const activeLength = activeList.length;

    useEffect(() => {
        if (!showDropdown) {
            setSelectedIndex(0);
            setDropdownMode(null);
        }
    }, [showDropdown]);

    const convertIdsToDisplay = useCallback(
        (storedValue) => convertIdsToDisplayUtil(storedValue, suggestions, descriptions),
        [suggestions, descriptions]
    );

    const fileRefsToDisplay = useCallback(
        (str) => {
            if (!str || typeof str !== 'string' || fileSuggestions.length === 0) return str;
            const norm = (x) => String(x).trim().toLowerCase();
            const byId = new Map(fileSuggestions.map((f) => [norm(f.id), f.name]));
            return str.replace(/@\[([^\]]+)\]/g, (_, id) => byId.get(norm(id)) ?? `@[${id}]`);
        },
        [fileSuggestions]
    );

    const fileNamesToRefs = useCallback(
        (str) => {
            if (!str || typeof str !== 'string' || fileSuggestions.length === 0) return str;
            const sorted = [...fileSuggestions].sort((a, b) => (b.name?.length ?? 0) - (a.name?.length ?? 0));
            let out = str;
            for (const f of sorted) {
                if (f.name) {
                    const escaped = f.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    out = out.replace(new RegExp(escaped, 'g'), `@[${f.id}]`);
                }
            }
            return out;
        },
        [fileSuggestions]
    );

    const convertDisplayToIds = useCallback(
        (displayValue) => {
            const withFileRefs = fileNamesToRefs(displayValue);
            return convertDisplayToIdsUtil(withFileRefs, suggestions, descriptions);
        },
        [suggestions, descriptions, fileNamesToRefs]
    );

    useEffect(() => {
        if (readOnly || disabled) return;
        const needsMigration =
            value &&
            typeof value === 'string' &&
            migratedValueRef.current !== value &&
            migrateOldVariableFormat(value, suggestions, descriptions) !== value;

        if (needsMigration) {
            const convertedValue = migrateOldVariableFormat(value, suggestions, descriptions);
            if (convertedValue !== value && onChange && inputRef.current) {
                migratedValueRef.current = value;
                onChange({
                    target: { value: convertedValue, name: inputRef.current.name },
                });
            }
        } else if (!value || typeof value !== 'string' || !value.includes('${')) {
            migratedValueRef.current = null;
        }
    }, [value, onChange, suggestions, descriptions, readOnly, disabled]);

    const displayValue = useMemo(() => {
        const withVariables = convertIdsToDisplay(value);
        return fileRefsToDisplay(withVariables);
    }, [value, convertIdsToDisplay, fileRefsToDisplay]);

    useEffect(() => {
        if (showDropdown) {
            setDropdownPosition(calculateDropdownPosition(inputRef.current));
        }
    }, [showDropdown]);

    const resolveDropdownState = useCallback(
        (val, cursor) =>
            getDropdownState({
                val,
                cursor,
                readOnly,
                disabled,
                variableTrigger,
                fileTrigger,
                suggestionsLength: suggestions.length,
                fileSuggestionsLength: fileSuggestions.length,
            }),
        [readOnly, disabled, variableTrigger, fileTrigger, suggestions.length, fileSuggestions.length]
    );

    const { insertFile, insertVariable } = useSmartInputInsertions({
        inputRef,
        fileTrigger,
        variableTrigger,
        descriptions,
        formatFileInsertion,
        onChange,
        convertDisplayToIds,
        setShowDropdown,
    });

    const insertToken = useCallback(
        (item) => {
            if (dropdownMode === 'files') insertFile(item);
            else insertVariable(item);
        },
        [dropdownMode, insertFile, insertVariable]
    );

    const scrollSelectedIntoView = useCallback((index) => {
        if (dropdownListRef.current) {
            const li = dropdownListRef.current.children[index];
            if (li) li.scrollIntoView({ block: 'nearest' });
        }
    }, []);

    const handleChange = useCallback(
        (e) => {
            if (readOnly || disabled) return;
            const input = e.target;
            const nextDisplayValue = e.target.value;
            if (!inputRef.current) inputRef.current = input;
            const storedValue = convertDisplayToIds(nextDisplayValue);
            onChange({ target: { ...e.target, value: storedValue } });
            setTimeout(() => {
                const cursor = input.selectionStart;
                const { show, mode } = resolveDropdownState(nextDisplayValue, cursor);
                setShowDropdown(show);
                setDropdownMode(mode);
                if (show) setSelectedIndex(0);
            }, 0);
        },
        [readOnly, disabled, onChange, resolveDropdownState, convertDisplayToIds]
    );

    const handleBlur = useCallback(
        (e) => {
            const storedValue = convertDisplayToIds(e.target.value);
            onBlur?.({ target: { ...e.target, value: storedValue } });
        },
        [onBlur, convertDisplayToIds]
    );

    const handlePaste = useCallback(
        (e) => {
            if (readOnly || disabled || !onMultiLinePaste) return;

            const text = e.clipboardData?.getData('text') ?? '';
            if (!text.includes('\n')) return;

            const lines = text
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter(Boolean);

            // Trailing newline on a single copied line — let native paste preserve cursor/text.
            if (lines.length <= 1) return;

            e.preventDefault();

            const input = e.target;
            const displayText = input.value ?? '';
            const selectionStart = input.selectionStart ?? displayText.length;
            const selectionEnd = input.selectionEnd ?? displayText.length;
            const before = displayText.slice(0, selectionStart);
            const after = displayText.slice(selectionEnd);

            const extraLines = lines.slice(1);
            const lastExtraIndex = extraLines.length - 1;
            const extraLinesStored = extraLines.map((line, i) =>
                convertDisplayToIds(i === lastExtraIndex ? line + after : line)
            );

            onMultiLinePaste({
                firstLine: convertDisplayToIds(before + lines[0]),
                extraLines: extraLinesStored,
            });
        },
        [readOnly, disabled, onMultiLinePaste, convertDisplayToIds]
    );

    const handleKeyDown = useCallback(
        (e) => {
            parentOnKeyDown?.(e);
            if (readOnly || disabled) {
                setShowDropdown(false);
                return;
            }

            if (showDropdown && activeLength > 0) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIndex((prev) => {
                        const next =
                            e.key === 'ArrowDown'
                                ? (prev + 1) % activeLength
                                : prev === 0
                                  ? activeLength - 1
                                  : prev - 1;
                        requestAnimationFrame(() => scrollSelectedIntoView(next));
                        return next;
                    });
                    return;
                }
                if (e.key === 'Enter') {
                    e.preventDefault();
                    insertToken(activeList[selectedIndex]);
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    setShowDropdown(false);
                    return;
                }
            }

            const input = inputRef.current;
            if (!input || input.selectionStart !== input.selectionEnd) return;

            const inputDisplayValue = input.value;
            const cursor = input.selectionStart;
            const isBackspace = e.key === 'Backspace' && cursor > 0;
            const isDelete = e.key === 'Delete' && cursor < inputDisplayValue.length;

            if (isBackspace || isDelete) {
                const checkCursor = isBackspace ? cursor - 1 : cursor;
                let varInfo = findVariableAtCursor(inputDisplayValue, checkCursor);
                if (!varInfo) {
                    varInfo = findFileSegmentInDisplay(inputDisplayValue, checkCursor, fileSuggestions);
                }
                if (!varInfo) {
                    varInfo = findFileRefAtCursor(inputDisplayValue, checkCursor, fileTrigger);
                }
                if (varInfo) {
                    e.preventDefault();
                    const newDisplayValue =
                        inputDisplayValue.slice(0, varInfo.varStart) +
                        inputDisplayValue.slice(varInfo.varEnd);
                    const newStoredValue = convertDisplayToIds(newDisplayValue);
                    onChange({ target: { value: newStoredValue, name: input.name } });
                    requestAnimationFrame(() => {
                        input.focus();
                        input.selectionStart = input.selectionEnd = varInfo.varStart;
                    });
                }
            }
        },
        [
            readOnly,
            disabled,
            showDropdown,
            activeList,
            activeLength,
            selectedIndex,
            insertToken,
            scrollSelectedIntoView,
            onChange,
            convertDisplayToIds,
            parentOnKeyDown,
            fileTrigger,
            fileSuggestions,
        ]
    );

    const handleOutsideClick = useCallback((e) => {
        const isOutsideInput = containerRef.current && !containerRef.current.contains(e.target);
        const isOutsideDropdown =
            !dropdownPortalRef.current || !dropdownPortalRef.current.contains(e.target);
        if (isOutsideInput && isOutsideDropdown) setShowDropdown(false);
    }, []);

    const updatePosition = useCallback(() => {
        if (showDropdown) setDropdownPosition(calculateDropdownPosition(inputRef.current));
    }, [showDropdown]);

    useEffect(() => {
        if (!showDropdown) return undefined;
        document.addEventListener('mousedown', handleOutsideClick);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [showDropdown, handleOutsideClick, updatePosition]);

    const activeIndex = Math.min(selectedIndex, Math.max(0, activeLength - 1));

    return {
        containerRef,
        inputRef,
        dropdownListRef,
        dropdownPortalRef,
        displayValue,
        showDropdown,
        dropdownMode,
        dropdownPosition,
        activeLength,
        activeIndex,
        handleChange,
        handleBlur,
        handlePaste,
        handleKeyDown,
        insertToken,
        setSelectedIndex,
    };
}
